import React from "react";
import { Link } from "react-router-dom";
import { CRow, CCol, CCard, CCardHeader, CCardBody, CFormCheck, CBadge, CButton } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMap, cilDescription, cilPencil, cilTrash, cilVideo, cilSpeedometer, cilFingerprint, cilFile } from "@coreui/icons";
import SafeTooltip from "../../../components/SafeTooltip/SafeTooltip";
import dayjs from "dayjs";
import { RenderVehicleIcon } from "./FilterControls";

// --- Встроенные стили для современного "виджетного" дизайна ---
const customStyles = `
  .modern-device-card {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    border-radius: 16px !important;
    border: 1px solid rgba(0,0,0,0.05) !important;
    background-color: #f8f9fa !important;
  }
  .modern-device-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.1) !important;
  }
  .modern-device-card.selected {
    outline: 2px solid #5856d6;
    outline-offset: 2px;
    box-shadow: 0 8px 24px rgba(88, 86, 214, 0.25) !important;
    border-color: transparent !important;
  }
  .info-widget {
    background: #ffffff;
    border: 1px solid rgba(0,0,0,0.04);
    border-radius: 12px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
  }
  .btn-modern-action {
    border-radius: 10px !important;
    transition: all 0.2s;
  }
  .btn-modern-action:hover {
    transform: scale(1.05);
  }
`;

const checkIsDumpTruck = (dev) => {
  if (!dev) return false;
  const typeName = (dev.vehicle_type_name || "").toLowerCase();
  const devName = (dev.name || "").toLowerCase();
  return (
    typeName.includes("самосвал") ||
    typeName.includes("dump") ||
    typeName.includes("truck") ||
    devName.includes("самосвал") ||
    dev.vehicle_type === 3
  );
};

const DeviceGrid = ({
  t,
  devices,
  processedDevices,
  selectedDevices,
  toggleDeviceSelection,
  getDeviceStatus,
  isStaff,
  handleEditClick,
  handleDeleteClick,
  handleViewPassport,
}) => {
  return (
    <>
      <style>{customStyles}</style>

      <CRow className="g-4">
        {processedDevices.map((dev) => {
          const status = getDeviceStatus(dev);
          const isOfflineDevice = dev.mac_address && dev.mac_address.startsWith("OFFLINE-");
          const isSelected = selectedDevices.includes(dev.id);
          const isOnline = status.color === "success";

          return (
            <CCol xl={4} lg={6} md={6} sm={12} key={dev.id}>
              <CCard
                className={`h-100 shadow-sm modern-device-card ${isSelected ? "selected" : ""}`}
                onClick={() => toggleDeviceSelection(dev.id)}
                style={{ cursor: "pointer" }}
              >
                {/* ШАПКА КАРТОЧКИ */}
                <CCardHeader
                  className="d-flex justify-content-between align-items-center bg-white py-3 border-bottom text-dark"
                  style={{ borderTopLeftRadius: "15px", borderTopRightRadius: "15px" }}
                >
                  <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                    <CFormCheck
                      id={`check-${dev.id}`}
                      checked={isSelected}
                      onChange={() => toggleDeviceSelection(dev.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mb-0 m-0 p-0 flex-shrink-0"
                      style={{ cursor: "pointer", transform: "scale(1.3)", transformOrigin: "left center" }}
                    />
                    <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                      <label
                        htmlFor={`check-${dev.id}`}
                        className="mb-0 text-truncate fw-bolder fs-5"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        title={dev.name}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dev.name}
                      </label>
                      {/* Иконка и тип техники из базы данных */}
                      <span className="small text-muted fw-semibold d-flex align-items-center gap-1 mt-1">
                        <RenderVehicleIcon
                          icon={dev.vehicle_type_icon?.startsWith("http") ? null : dev.vehicle_type_icon}
                          iconUrl={dev.vehicle_type_icon?.startsWith("http") ? dev.vehicle_type_icon : null}
                        />
                        <span>{dev.vehicle_type_name || t("device_grid.technology", "Техника")}</span>
                      </span>
                    </div>
                  </div>
                  <SafeTooltip content={`Активность: ${dev.last_seen ? dayjs(dev.last_seen).format("DD.MM.YY HH:mm") : "Никогда"}`}>
                    <CBadge color={status.color} shape="rounded-pill" className="ms-2 px-3 py-2 shadow-sm flex-shrink-0 fw-bold">
                      {status.text}
                    </CBadge>
                  </SafeTooltip>
                </CCardHeader>

                {/* ТЕЛО КАРТОЧКИ (Виджеты) */}
                <CCardBody className="d-flex flex-column p-3 gap-3">
                  {/* Блок 1: Локация (Организация и Участок) */}
                  <div className="info-widget d-flex flex-column gap-2">
                    {dev.organization_name && (
                      <div className="text-dark fw-bolder text-truncate" style={{ fontSize: "1.1rem" }} title={dev.organization_name}>
                        {dev.organization_name}
                      </div>
                    )}
                    <div className="d-flex align-items-center">
                      <CIcon icon={cilMap} className={dev.work_zone_name ? "text-primary me-2" : "text-muted me-2"} size="sm" />
                      <span className={dev.work_zone_name ? "small fw-semibold text-primary text-truncate" : "small text-muted fst-italic text-truncate"}>
                        {dev.work_zone_name || t("device_grid.zone_not_assigned", "Участок не назначен")}
                      </span>
                    </div>
                  </div>

                  {/* Блок 2: Спецификации */}
                  <div className="info-widget d-flex flex-column gap-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small d-flex align-items-center">
                        <CIcon icon={cilSpeedometer} className="me-2" />
                        {checkIsDumpTruck(dev)
                          ? t("device_grid.body_volume", "Объем кузова:")
                          : t("device_grid.bucket_volume", "Объем ковша:")}
                      </span>
                      <strong className="text-dark">{dev.bucket_volume} м³</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small d-flex align-items-center">
                        <CIcon icon={cilFingerprint} className="me-2" />
                        {t("device_grid.mac_address", "MAC-адрес:")}
                      </span>
                      <span className="font-monospace text-muted small text-truncate ms-2" title={dev.mac_address}>
                        {dev.mac_address}
                      </span>
                    </div>
                  </div>

                  {/* Блок 3: Статус сети, Срок службы и Лицензия */}
                  <div className={`info-widget mt-auto d-flex flex-column gap-2 ${isOnline ? "border-success border-opacity-50 bg-success bg-opacity-10" : ""}`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className={`fw-bold small ${isOnline ? "text-success" : "text-muted"}`}>
                        {t("device_grid.online", "В сети:")}
                      </span>
                      <strong className={`${isOnline ? "text-success" : "text-dark"}`} style={{ fontSize: "0.9rem" }}>
                        {dev.last_seen ? dayjs(dev.last_seen).format("DD.MM.YY в HH:mm") : "Никогда"}
                      </strong>
                    </div>

                    {/* Срок службы (24 месяца со дня привязки к клиенту) */}
                    {dev.warranty_end_date && (
                      <div className="d-flex justify-content-between align-items-center pt-1 border-top border-secondary border-opacity-10">
                        <span className="text-muted small">{t("device_grid.warranty_period", "Срок службы:")}</span>
                        <strong
                          className={`small ${
                            dev.warranty_days_left <= 7
                              ? "text-danger fw-bold"
                              : dev.warranty_days_left <= 30
                              ? "text-warning fw-bold"
                              : "text-dark"
                          }`}
                        >
                          до {dayjs(dev.warranty_end_date).format("DD.MM.YYYY")}
                          {dev.warranty_days_left !== null && dev.warranty_days_left !== undefined && dev.warranty_days_left <= 30 && (
                            <span className="ms-1">
                              ({dev.warranty_days_left > 0 ? `${dev.warranty_days_left} дн.` : "истёк"})
                            </span>
                          )}
                        </strong>
                      </div>
                    )}

                    {dev.license_end_date && (
                      <div className="d-flex justify-content-between align-items-center pt-1 border-top border-secondary border-opacity-10">
                        <span className="text-muted small">{t("device_grid.license", "Лицензия:")}</span>
                        <strong className={`small ${dayjs(dev.license_end_date).isBefore(dayjs()) ? "text-danger" : "text-muted"}`}>
                          до {dayjs(dev.license_end_date).format("DD.MM.YY")}
                        </strong>
                      </div>
                    )}
                  </div>

                  {/* КНОПКИ ДЕЙСТВИЙ (Внизу) */}
                  <div className="pt-2 d-flex gap-2">
                    <Link
                      to={`/camera/${dev.id}`}
                      className="text-decoration-none flex-grow-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CButton color="primary" className="w-100 fw-bold d-flex justify-content-center align-items-center text-white shadow-sm btn-modern-action">
                        <CIcon icon={cilVideo} className="me-2" /> {t("device_grid.open", "Открыть")}
                      </CButton>
                    </Link>

                    <CButton
                      color="info"
                      variant="outline"
                      className="btn-modern-action px-3"
                      title={t("device_grid.technical_passport", "Технический паспорт")}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPassport(dev.id, dev.name);
                      }}
                    >
                      <CIcon icon={cilDescription} />
                    </CButton>

                    {dev.document_1 && (
                      <CButton
                        color="warning"
                        variant="outline"
                        className="btn-modern-action px-3"
                        title={t("device_grid.license_agreement", "Лицензионный договор")}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(dev.document_1, "_blank");
                        }}
                      >
                        <CIcon icon={cilFile} />
                      </CButton>
                    )}

                    {isStaff && (
                      <>
                        <CButton
                          color="secondary"
                          variant="outline"
                          className="btn-modern-action px-3"
                          title={t("device_grid.edit", "Редактировать")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(dev);
                          }}
                        >
                          <CIcon icon={cilPencil} />
                        </CButton>
                        <CButton
                          color="danger"
                          variant="outline"
                          className="btn-modern-action px-3"
                          title={
                            !isOfflineDevice
                              ? t("device_grid.deletion_not_allowed", "Удаление реальных устройств запрещено")
                              : t("device_grid.delete", "Удалить")
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(dev);
                          }}
                          disabled={!isOfflineDevice}
                        >
                          <CIcon icon={cilTrash} />
                        </CButton>
                      </>
                    )}
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          );
        })}

        {devices.length === 0 && (
          <CCol xs={12} className="text-center text-muted mt-5 bg-white p-5 rounded-4 border shadow-sm">
            <h4>{t("device_grid.no_devices", "Устройств пока нет")}</h4>
            <p>{t("device_grid.add_first_excavator", "Добавьте первый экскаватор, чтобы начать работу.")}</p>
          </CCol>
        )}

        {devices.length > 0 && processedDevices.length === 0 && (
          <CCol xs={12} className="text-center text-muted mt-5">
            <h5>{t("device_grid.no_results_found", "По вашему запросу ничего не найдено")}</h5>
          </CCol>
        )}
      </CRow>
    </>
  );
};

export default DeviceGrid;